/**
 * Lighting rig (PRD §3.4, §6.3 — voxel content, non-voxel lighting).
 *
 * Two presets:
 *   - "p1-basic": ambient + directional. Cheap, readable, no shadows.
 *     Use it in stories where you want to verify scene wiring without
 *     paying for SSAO/Bloom.
 *   - "magica-voxel": the canonical premium-quality preset. Warm key
 *     + cool sky ambient + warm interior HDRI + soft shadows, tuned
 *     to evoke a MagicaVoxel render. Shadow-side voxel faces still
 *     read color instead of crushing to black; the warm/cool cross-
 *     color split is the signature voxel-render look.
 *
 * Kill-pulse (FE-30 P6): when `killPulseStartedAt` is set, the key
 * directional light's intensity + color briefly thumps toward warm-
 * amber over `KILL_LIGHT_PULSE_DURATION_MS` then decays back. We
 * mutate refs imperatively so no React re-render fires per frame.
 */

import { useEffect, useRef } from "react";
import { Color, type DirectionalLight, type AmbientLight, type HemisphereLight } from "three";
import { Environment } from "@react-three/drei";
import { useFrame } from "@react-three/fiber";
import { killPulseState, KILL_LIGHT_WARM_COLOR } from "./killLight";

export type LightingPreset = "p1-basic" | "magica-voxel";

interface LightsProps {
  preset?: LightingPreset;
  /** `performance.now()` at the rising edge of a kill event. `null` =
   *  no active pulse. The pulse self-expires; consumers can re-set
   *  this on a later kill to re-trigger. */
  killPulseStartedAt?: number | null;
}

const BASIC_KEY_BASE_INTENSITY = 1.0;
const BASIC_KEY_BASE_COLOR = "#ffffff";
const BASIC_AMBIENT_BASE_INTENSITY = 0.4;

// MagicaVoxel-tuned preset (FE-30). The signature MV render look is
// path-traced GI: strong AO in seams (we get this from SSAO downstream
// in PostFx) + clean color separation per voxel face + soft warm/cool
// palette balance. Tuning:
//   - Moderate ambient (~40% above no-ambient) keeps shadow-side voxel
//     faces colored instead of crushing to black.
//   - Warm key (`#fff2d6` ≈ "afternoon sun") creates a clean cross-
//     color split with the cool sky ambient — the hallmark MV vibe.
//   - Lower key angle (Y=10) gives longer, more dramatic shadows that
//     read the voxel structure.
//   - "Apartment" HDRI dialed sub-unit so the indirect contribution
//     adds reflection tint without flooding the scene.
const MV_KEY_BASE_INTENSITY = 1.3;
const MV_KEY_BASE_COLOR = "#fff2d6";
const MV_AMBIENT_BASE_INTENSITY = 0.35;
const MV_AMBIENT_COLOR = "#d4e0ff";
const MV_HEMI_INTENSITY = 0.3;
const MV_ENV_INTENSITY = 0.55;

const _baseColor = new Color(BASIC_KEY_BASE_COLOR);
const _warmColor = new Color(KILL_LIGHT_WARM_COLOR);

interface LightBaselines {
  keyIntensity: number;
  keyColor: string;
  ambientIntensity: number;
  ambientColor: string;
  hemiIntensity: number;
}

// Per-preset baseline values consumed by the preset-switch effect AND
// the kill-pulse useFrame loop. Pure function of `preset` so it lives
// outside the component — no allocation per render.
function baselinesFor(preset: LightingPreset): LightBaselines {
  if (preset === "magica-voxel") {
    return {
      keyIntensity: MV_KEY_BASE_INTENSITY,
      keyColor: MV_KEY_BASE_COLOR,
      ambientIntensity: MV_AMBIENT_BASE_INTENSITY,
      ambientColor: MV_AMBIENT_COLOR,
      hemiIntensity: MV_HEMI_INTENSITY,
    };
  }
  return {
    keyIntensity: BASIC_KEY_BASE_INTENSITY,
    keyColor: BASIC_KEY_BASE_COLOR,
    ambientIntensity: BASIC_AMBIENT_BASE_INTENSITY,
    ambientColor: "#ffffff",
    hemiIntensity: 0,
  };
}

export function Lights({ preset = "magica-voxel", killPulseStartedAt = null }: LightsProps) {
  const keyRef = useRef<DirectionalLight>(null);
  const ambientRef = useRef<AmbientLight>(null);
  const hemisphereRef = useRef<HemisphereLight>(null);

  // Reset to baseline when the preset switches (e.g., stories toggling).
  useEffect(() => {
    const b = baselinesFor(preset);
    if (keyRef.current) {
      keyRef.current.intensity = b.keyIntensity;
      keyRef.current.color.set(b.keyColor);
    }
    if (ambientRef.current) {
      ambientRef.current.intensity = b.ambientIntensity;
      ambientRef.current.color.set(b.ambientColor);
    }
    if (hemisphereRef.current) {
      hemisphereRef.current.intensity = b.hemiIntensity;
    }
  }, [preset]);

  useFrame(() => {
    const pulse = killPulseState(killPulseStartedAt ?? null, performance.now());
    const b = baselinesFor(preset);
    if (keyRef.current) {
      keyRef.current.intensity = b.keyIntensity * pulse.intensityMul;
      _baseColor.set(b.keyColor);
      keyRef.current.color.copy(_baseColor).lerp(_warmColor, pulse.warmBlend);
    }
    if (ambientRef.current) {
      ambientRef.current.intensity = b.ambientIntensity * pulse.ambientMul;
    }
    if (hemisphereRef.current) {
      hemisphereRef.current.intensity = b.hemiIntensity * pulse.ambientMul;
    }
  });

  if (preset === "p1-basic") {
    return (
      <>
        <ambientLight ref={ambientRef} intensity={BASIC_AMBIENT_BASE_INTENSITY} />
        <directionalLight
          ref={keyRef}
          position={[6, 10, 6]}
          intensity={BASIC_KEY_BASE_INTENSITY}
        />
      </>
    );
  }

  return (
    <>
      {/* "Apartment" HDRI for reflections — warm interior tones,
          dimmer than `park` or `city`. Background stays off and we
          dial intensity below 1.0 to keep the HDRI from over-
          saturating the scene; the direct lights drive the look. */}
      <Environment
        preset="apartment"
        background={false}
        environmentIntensity={MV_ENV_INTENSITY}
      />

      {/* Warm key at a moderate elevation for long, structure-
          revealing voxel shadows. */}
      <directionalLight
        ref={keyRef}
        position={[6, 10, 4]}
        intensity={MV_KEY_BASE_INTENSITY}
        color={MV_KEY_BASE_COLOR}
        castShadow
        shadow-mapSize-width={2048}
        shadow-mapSize-height={2048}
        shadow-camera-left={-20}
        shadow-camera-right={20}
        shadow-camera-top={20}
        shadow-camera-bottom={-20}
        shadow-bias={-0.0003}
      />

      {/* Cool ambient — the warm/cool split is the MV signature.
          Keeps shadow-side faces readable in color instead of going
          to near-black. */}
      <ambientLight
        ref={ambientRef}
        intensity={MV_AMBIENT_BASE_INTENSITY}
        color={MV_AMBIENT_COLOR}
      />

      {/* Hemispheric: cool sky, warm earth. Reinforces the warm/cool
          split and tints floor-facing voxel undersides correctly. */}
      <hemisphereLight
        ref={hemisphereRef}
        args={["#e0e8ff", "#7a5a3a", MV_HEMI_INTENSITY]}
      />
    </>
  );
}
